import {createContext, useCallback, useEffect, useState} from 'react';
import axios from "axios";
import {useCookies} from "react-cookie";

const TokenContext = createContext(null)

// Enregistré immédiatement à l'import (avant tout rendu React) pour éviter la course
// entre l'intercepteur (posé dans un useEffect de TokenProvider) et les fetchs déclenchés
// par les useEffect des Provider enfants (qui s'exécutent avant ceux du parent).
let latestAccessToken = '';
axios.interceptors.request.use((config) => {
    if (latestAccessToken && validateToken(latestAccessToken)) {
        config.headers.Authorization = `Bearer ${latestAccessToken}`;
    }
    return config;
});

const COOKIE_OPTIONS = {
    httpOnly: false,
    secure: process.env.NODE_ENV === 'production',
    sameSite: 'lax',
    path: '/',
    maxAge: 60 * 60 * 24 * 7 // 7 jours
};

const generateSecureState = () => {
    const array = new Uint8Array(32);
    crypto.getRandomValues(array);
    return Array.from(array, byte => byte.toString(16).padStart(2, '0')).join('');
};

const validateToken = (token) => {
    if (!token || typeof token !== 'string') return false;

    try {
        if (!token.includes('.')) {
            // Token opaque (non-JWT), considéré comme valide
            return true;
        }

        const parts = token.split('.');
        if (parts.length !== 3) return false;

        const payload = JSON.parse(atob(parts[1]));
        const now = Math.floor(Date.now() / 1000);

        return payload.exp > now;
    } catch (error) {
        return true;
    }
};

function TokenProvider({children}) {
    const [cookies, setCookie, removeCookie] = useCookies(['QRCODE_ACCESS', 'QRCODE_REFRESH', 'QRCODE_EXPIRE', 'QRCODE_STATE']);
    const serverOauth = process.env.REACT_APP_API_URL || 'http://localhost:3040';
    const securityUrl = process.env.REACT_APP_SECURITY_URL || 'https://security.alais-studio.fr';

    const urlCode = '/auth/code'
    const urlAuthorize = '/auth/authorize'
    const urlToken = '/auth/token'

    const [authState] = useState(() => cookies.QRCODE_STATE || generateSecureState());
    const [accessToken, setAccessToken] = useState(() => {
        const token = cookies.QRCODE_ACCESS;
        return validateToken(token) ? token : '';
    })
    const [refreshToken, setRefreshToken] = useState(() => cookies.QRCODE_REFRESH || '')
    const [expireAt, setExpireAt] = useState(() => cookies.QRCODE_EXPIRE ? parseInt(cookies.QRCODE_EXPIRE) : 0)

    const [finishProcess, setFinishProcess] = useState(() => validateToken(cookies.QRCODE_ACCESS))
    const [authError, setAuthError] = useState(null)
    const [isLoading, setIsLoading] = useState(false)

    const [isRefreshing, setIsRefreshing] = useState(false)

    // Assignation synchrone (pendant le rendu, pas dans un effet) : garantit que
    // l'intercepteur voit le token à jour avant que les useEffect des enfants ne partent.
    latestAccessToken = accessToken;

    const saveToken = useCallback((access, refresh, expire) => {
        if (!validateToken(access)) {
            throw new Error('Token d\'accès invalide');
        }

        setAccessToken(access)
        setRefreshToken(refresh)
        setExpireAt(expire)

        setCookie("QRCODE_ACCESS", access, COOKIE_OPTIONS)
        setCookie("QRCODE_REFRESH", refresh, COOKIE_OPTIONS)
        setCookie("QRCODE_EXPIRE", expire.toString(), COOKIE_OPTIONS)
    }, [setCookie])

    const clearAuth = useCallback(() => {
        setAccessToken('')
        setRefreshToken('')
        setExpireAt(0)
        setFinishProcess(false)
        setAuthError(null)

        removeCookie("QRCODE_ACCESS")
        removeCookie("QRCODE_REFRESH")
        removeCookie("QRCODE_EXPIRE")
        removeCookie("QRCODE_STATE")
    }, [removeCookie])

    const startOAuthFlow = useCallback(() => {
        setCookie("QRCODE_STATE", authState, {...COOKIE_OPTIONS, maxAge: 60 * 10});
        window.location.href = `${serverOauth}${urlCode}?state=${encodeURIComponent(authState)}`;
    }, [authState, setCookie, serverOauth]);

    const setCode = useCallback(async (code, receivedState) => {
        if (finishProcess) return;

        setIsLoading(true);
        setAuthError(null);

        try {
            const storedState = cookies.QRCODE_STATE;
            if (!receivedState || receivedState !== storedState) {
                throw new Error('State invalide - CSRF détecté');
            }

            const response = await axios.post(`${serverOauth}${urlAuthorize}`, {code}, {
                timeout: 15000,
                headers: {'Content-Type': 'application/json', 'Accept': 'application/json'}
            });

            if (response.data?.error) {
                throw new Error(`Erreur serveur: ${response.data.error}`);
            }
            if (!response.data?.access_token) {
                throw new Error('Token d\'accès manquant dans la réponse serveur');
            }

            saveToken(response.data.access_token, response.data.refresh_token, response.data.expire_at);
            removeCookie("QRCODE_STATE");
            setFinishProcess(true);
        } catch (error) {
            const errorMessage = error.response?.data?.error_description ||
                error.response?.data?.error ||
                error.message ||
                'Erreur d\'authentification inconnue';
            setAuthError(errorMessage);
        } finally {
            setIsLoading(false);
        }
    }, [finishProcess, cookies.QRCODE_STATE, serverOauth, saveToken, removeCookie])

    const disconnect = useCallback(() => {
        clearAuth();
        window.location.href = `${securityUrl}/auth/disconnect`;
    }, [clearAuth, securityUrl])

    const regenerateAccessToken = useCallback(async () => {
        if (isRefreshing) return;

        const actualRefreshToken = refreshToken || cookies.QRCODE_REFRESH;
        if (!actualRefreshToken) {
            disconnect();
            return;
        }

        setIsRefreshing(true);

        try {
            const response = await axios.post(`${serverOauth}${urlToken}`, {
                refresh_token: actualRefreshToken
            }, {
                timeout: 10000,
                headers: {'Content-Type': 'application/json'}
            });

            if (response.data?.error || !response.data?.access_token) {
                throw new Error(response.data?.error || 'Erreur de renouvellement du token');
            }

            saveToken(response.data.access_token, response.data.refresh_token, response.data.expire_at);
        } catch (error) {
            disconnect();
        } finally {
            setTimeout(() => setIsRefreshing(false), 1000);
        }
    }, [refreshToken, serverOauth, saveToken, disconnect, cookies.QRCODE_REFRESH, isRefreshing])

    // Renouvellement automatique avant expiration (marge de 5 minutes)
    useEffect(() => {
        if (expireAt > 0 && accessToken) {
            const renewTime = expireAt - Date.now() - (5 * 60 * 1000);

            if (renewTime > 0) {
                const timeoutId = setTimeout(() => regenerateAccessToken(), renewTime);
                return () => clearTimeout(timeoutId);
            }
            regenerateAccessToken();
        }
    }, [expireAt, accessToken, regenerateAccessToken])

    // Intercepteur axios : refresh sur 401 (le header Bearer est géré par l'intercepteur
    // de requête enregistré au chargement du module, via `latestAccessToken`)
    useEffect(() => {
        if (!accessToken) return;

        const responseInterceptor = axios.interceptors.response.use(
            (response) => response,
            async (error) => {
                if (error.response?.status === 401 && !isRefreshing) {
                    await regenerateAccessToken();
                }
                return Promise.reject(error);
            }
        );

        return () => {
            axios.interceptors.response.eject(responseInterceptor);
        };
    }, [accessToken, regenerateAccessToken, isRefreshing])

    return (
        <TokenContext.Provider
            value={{
                accessToken,
                refreshToken,
                regenerateAccessToken,
                authState,
                startOAuthFlow,
                setCode,
                finishProcess,
                setFinishProcess,
                disconnect,
                clearAuth,
                authError,
                isLoading,
                isAuthenticated: !!accessToken && validateToken(accessToken)
            }}>
            {children}
        </TokenContext.Provider>
    );
};

export {TokenContext, TokenProvider};
